import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import cn from 'classnames';
import {NewLineBlock, OrderEta} from 'routes/App/ordersV2/OrdersList/styles';
import {
  DELIVERY_PROVIDER_TYPE,
  ORDER,
  ORDER_SOURCE,
  ORDER_STATUS,
  ORDER_TYPE,
  ORDER_TYPES
} from 'helpers/constants/orders_v2';
import {FlexContainer, FlexItem} from 'components/layout';
import UberEats from 'assets/uber-eats.svg';
import Doordash from 'assets/icon-doordash.svg';
import Grubhub from 'assets/icon-grubhub.svg';
import {groupDigital} from 'helpers/functionUtils/groupByDigital';
import StatusButton from 'routes/App/ordersV2/OrdersList/components/StatusButton';
import IconPrint from 'assets/IconPrint';
import {phoneFormatForView} from 'helpers/functionUtils/helpFunctions';
import {
  IconButtonPrinter,
  OrdersTableWrapper,
  SavingsIconButton
} from 'routes/App/ordersV2/components/OrdersTable/styles';
import {
  checkIsFullNotToSauceRefundOrder,
  checkIsFullRefundOrder,
  checkIsFutureOrder,
  checkIsRefundOrder
} from 'helpers/functionUtils/ordersUtilsV2';
import IconPickUpDefault from 'assets/icon-pickup-default.svg';
import IconCurbsidePickup from 'assets/icon-curbside-pickup.svg';
import IconDinein from 'assets/icon-dinein.svg';
import IconRestaurantDelivery from 'assets/icon-restaurant-delivery.svg';
import IconSauceDelivery from 'assets/icon-sauce-delivery.svg';
import {ButtonOutline} from 'components/buttons';
import {TableText} from 'components/TableComponent/styles';
import {FEATURE_RESTRICTION_IDS} from 'helpers/constants/restrictions';
import RestrictedArea from 'components/layout/RestrictedArea';
import OrdersTableComponent from 'components/OrdersTableComponentV2';

const getFieldsConfig = (
  onStatusChange,
  onOpenDetailsClick,
  onPrintClick,
  isNetworkOnline,
  changeStatusLoading,
  onDeliverWithUsClick,
  showSavingPigIcon,
  showDeliverWithUsButton
) => [
  {
    title: 'ETA',
    // sortable: true,
    onCellClick: onOpenDetailsClick,
    fieldName: 'CookingEndTime.Local',
    renderer: (data) => (
      <OrderEta>
        {checkIsFutureOrder(data) &&
          !checkIsFullNotToSauceRefundOrder(data) && (
            <span className='IconFutureOrder' />
          )}
        {(checkIsFullNotToSauceRefundOrder(data) ||
          (checkIsRefundOrder(data) && !checkIsFutureOrder(data))) && (
          <span className='IconRefundOrder' />
        )}
        {moment(`${data[ORDER.DATE]}Z`).utcOffset(0).format('MMMM D, h:mm A')}
      </OrderEta>
    )
  },
  {
    title: 'Order ID',
    onCellClick: onOpenDetailsClick,
    fieldName: ORDER.INVOICE_ID,
    renderer: (data) => {
      return (
        <div>
          {(() => {
            return data[ORDER.SOURCE] === ORDER_SOURCE.SAUCE
              ? data[ORDER.INVOICE_ID] || ''
              : data[ORDER.EXTERNAL_ID] || '';
          })()}
        </div>
      );
    }
  },
  {
    title: 'Customer',
    onCellClick: onOpenDetailsClick,
    renderer: (data) => (
      <FlexContainer className='TableCenter'>
        <NewLineBlock className='wordWrap'>
          {data[ORDER.CUSTOMER]?.fullName || ''}
        </NewLineBlock>
        {data[ORDER.CUSTOMER]?.phone && (
          <NewLineBlock>
            <a
              href={`tel:${data[ORDER.CUSTOMER]?.phone}`}
              alt='customer-phone-number'
              onClick={(e) => e.stopPropagation()}
            >
              {phoneFormatForView(data[ORDER.CUSTOMER]?.phone)}
            </a>
          </NewLineBlock>
        )}
      </FlexContainer>
    )
  },
  {
    title: 'Order type',
    onCellClick: onOpenDetailsClick,
    renderer: (data) => {
      return (
        <FlexContainer className='align-center orderProvider'>
          <FlexItem flex={1} className='marginRightXs orderIconContainer'>
            {(() => {
              if (data[ORDER.SOURCE] === ORDER_SOURCE.SAUCE) {
                return <img src={IconSauceDelivery} alt='logo' />;
              }
              if (data[ORDER.SOURCE] === ORDER_SOURCE.UBER) {
                return <img src={UberEats} alt='logo' />;
              }
              if (data[ORDER.SOURCE] === ORDER_SOURCE.DOORDASH) {
                return <img src={Doordash} alt='logo' />;
              }
              if (data[ORDER.SOURCE] === ORDER_SOURCE.GRUBHUB) {
                return <img src={Grubhub} alt='logo' />;
              }
              return <img src={IconSauceDelivery} alt='logo' />;
            })()}
          </FlexItem>
          <FlexItem className='marginRight'>
            {(() => {
              if (
                data[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY &&
                data[ORDER.DELIVERY]?.provider ===
                  DELIVERY_PROVIDER_TYPE.LOCATION
              ) {
                return <TableText>Self-delivery</TableText>;
              }
              if (data[ORDER.ORDER_TYPE] === ORDER_TYPE.DINE_IN) {
                return <TableText>Dine in</TableText>;
              }
              if (data[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY) {
                return <TableText>Delivery</TableText>;
              }
              return <TableText>Pickup</TableText>;
            })()}
          </FlexItem>
        </FlexContainer>
      );
    }
  },
  {
    title: 'Amount',
    onCellClick: onOpenDetailsClick,
    renderer: (data) => (
      <div>{`$${groupDigital(
        Number(data[ORDER.TOTALS]?.locationSubtotal || 0).toFixed(2)
      )} ${data[ORDER.PAYMENT_METHOD] || ''}`}</div>
    )
  },
  {
    title: 'Status',
    fieldName: 'OrderStatus',
    renderer: (data) => {
      return (
        <div
          style={
            !isNetworkOnline ||
            changeStatusLoading === data[ORDER.ID] ||
            data[ORDER.ORDER_STATUS] === ORDER_STATUS.CANCELLED
              ? {opacity: '0.5', pointerEvents: 'none'}
              : null
          }
        >
          <StatusButton
            order={data}
            onStatusChange={(status) => {
              onStatusChange(
                data[ORDER.ID],
                status,
                data[ORDER.ORDER_STATUS],
                data[ORDER.DELIVERY]?.isDispatch,
                data[ORDER.SOURCE]
              );
            }}
          />
        </div>
      );
    }
  },
  {
    renderer: (item) => (
      <FlexContainer className='justify-end'>
        <IconButtonPrinter
          data-gtm='printButton'
          onClick={() => onPrintClick(item)}
        >
          <IconPrint data-gtm='printButton' />
        </IconButtonPrinter>
      </FlexContainer>
    )
  }
];

const OrdersTable = ({
  onStatusChange,
  openDetails,
  printOrder,
  orders,
  loading,
  onSort,
  orderBy,
  orderDirection,
  isNetworkOnline,
  changeStatusLoading,
  onDeliverWithUsClick,
  showSavingPigIcon,
  showDeliverWithUsButton,
  isOrdersTable
}) => {
  return (
    <OrdersTableWrapper
      className={cn('OrdersTableWrapper', {
        NoOrders: !orders.length && isOrdersTable
      })}
    >
      <OrdersTableComponent
        fields={getFieldsConfig(
          onStatusChange,
          openDetails,
          printOrder,
          isNetworkOnline,
          changeStatusLoading,
          onDeliverWithUsClick,
          showSavingPigIcon,
          showDeliverWithUsButton
        )}
        data={orders}
        loading={loading}
        rowKeyPath={ORDER.ID}
        onSort={onSort}
        orderBy={orderBy}
        orderDirection={orderDirection}
      />
    </OrdersTableWrapper>
  );
};

OrdersTable.propTypes = {
  onStatusChange: PropTypes.func.isRequired,
  openDetails: PropTypes.func.isRequired,
  printOrder: PropTypes.func.isRequired,
  orders: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  isNetworkOnline: PropTypes.bool.isRequired,
  onSort: PropTypes.func,
  orderBy: PropTypes.string,
  orderDirection: PropTypes.string,
  changeStatusLoading: PropTypes.string.isRequired,
  onDeliverWithUsClick: PropTypes.func.isRequired,
  showSavingPigIcon: PropTypes.bool.isRequired,
  showDeliverWithUsButton: PropTypes.bool.isRequired,
  isOrdersTable: PropTypes.bool
};

OrdersTable.defaultProps = {
  onSort: () => {},
  orderBy: '',
  orderDirection: '',
  isOrdersTable: false
};

export default OrdersTable;
